/**
 * Layout Container Component
 * 
 * Container component that connects the Layout component to Redux store.
 * Manages layout-related state and provides handlers for navigation and
 * authentication actions. Acts as a bridge between the UI layer and state management.
 */

import React from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setActiveTab } from '../store/navigationSlice';
import { logout } from '../store/authSlice';
import { authService } from '../services/authService';
import Layout from '../components/layout/Layout';
import type { NavigationTab } from '../types';

/**
 * Props interface for LayoutContainer component
 */
interface LayoutContainerProps {
  children: React.ReactNode; // Child components to render within the layout
}

/**
 * LayoutContainer Component
 * 
 * Smart container that provides Redux integration for the Layout component.
 * Handles navigation state changes and user authentication actions.
 * Ensures the layout only renders when a user is authenticated.
 * 
 * @param children - Child components to render in the main content area
 * @returns JSX element containing the connected Layout component or null
 */
const LayoutContainer: React.FC<LayoutContainerProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  
  // Get current user from Redux store
  const { user } = useAppSelector((state) => state.auth);

  /**
   * Handle navigation tab changes
   * 
   * Dispatches action to update active tab in navigation state
   * 
   * @param tab - The navigation tab to activate
   */
  const handleTabChange = (tab: NavigationTab) => {
    dispatch(setActiveTab(tab));
  };

  /**
   * Handle user logout
   * 
   * Uses authService to properly clear all authentication data
   * and dispatches logout action to Redux store
   */
  const handleLogout = async () => {
    try {
      // Call authService logout to clear tokens and make API call
      await authService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Always clear Redux state and local storage
      dispatch(logout());
      authService.clearAuth();
    }
  };

  // Don't render layout if no user is authenticated
  if (!user) {
    return null;
  }

  return (
    <Layout
      user={user}
      onTabChange={handleTabChange}
      onLogout={handleLogout}
    >
      {children}
    </Layout>
  );
};

export default LayoutContainer;
